import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Settings, Plus, Edit, Trash2, RefreshCw, Shield, 
  Lock, Mail, User, Save, X, CheckCircle, XCircle, FileText, Star, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import './Admin.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  
  // User management state
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'viewer'
  });

  // Cambio de email state
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [changeEmailUser, setChangeEmailUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [changeEmailConfirm, setChangeEmailConfirm] = useState(false);

  // Asesores CNC state
  const [asesoresCNC, setAsesoresCNC] = useState([]);
  const [showAsesorModal, setShowAsesorModal] = useState(false);
  const [editingAsesor, setEditingAsesor] = useState(null);
  const [asesorForm, setAsesorForm] = useState({
    nombre_completo: '',
    email: '',
    telefono: '',
    cargo: '',
    es_analista: true,
    user_id: '',
    grupo_id: '',
    instituciones_asignadas: [],
    activo: true
  });
  const [usuariosEditores, setUsuariosEditores] = useState([]);

  // Asesores Probidad state
  const [asesoresProbidad, setAsesoresProbidad] = useState([]);
  const [showAsesorProbidadModal, setShowAsesorProbidadModal] = useState(false);
  const [editingAsesorProbidad, setEditingAsesorProbidad] = useState(null);
  const [asesorProbidadForm, setAsesorProbidadForm] = useState({
    nombre_completo: '',
    email: '',
    telefono: '',
    cargo: '',
    institucion_id: '',
    activo: true
  });
  const [instituciones, setInstituciones] = useState([]);

  // Indicadores Banderas Rojas state
  const [indicadoresBR, setIndicadoresBR] = useState([]);
  const [showIndicadorModal, setShowIndicadorModal] = useState(false);
  const [editingIndicador, setEditingIndicador] = useState(null);
  const [indicadorForm, setIndicadorForm] = useState({
    nombre_categoria: '',
    ejemplo_identificacion: '',
    forma_medicion: '',
    indicador: '',
    fuente_informacion: '',
    nivel_riesgo: 'Medio',
    justificacion_contextual: ''
  });

  // Bitácora state
  const [bitacora, setBitacora] = useState({ entradas_gestiones: [], eventos_sistema: [] });

  // Gestión de Datos state
  const [estadisticasSistema, setEstadisticasSistema] = useState(null);
  const [gestionesEliminadas, setGestionesEliminadas] = useState([]);

  // Calificaciones state
  const [calificacionesInstancias, setCalificacionesInstancias] = useState({ calificaciones: [], promedios: [] });
  const [calificacionesAsesores, setCalificacionesAsesores] = useState({ calificaciones: [], promedios: [] });
  
  // Grupos state
  const [grupos, setGrupos] = useState([]);
  const [showGrupoModal, setShowGrupoModal] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState(null);
  const [grupoForm, setGrupoForm] = useState({
    nombre: '',
    instituciones_ids: []
  });
  
  // Catalog management state
  const [catalogs, setCatalogs] = useState({
    institutions: [],
    categories: [],
    networks: [],
    persons: [],
    statuses: [],
    tipo_concurso: [],
    estado_concurso: [],
    cuentas_sociales: []
  });
  const [selectedCatalog, setSelectedCatalog] = useState('institutions');
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [catalogForm, setCatalogForm] = useState({
    value: '',
    order: 0,
    red_social: '',
    is_active: true
  });

  // Text templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    content: ''
  });

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'catalogs') {
      fetchAllCatalogs();
    } else if (activeTab === 'templates') {
      fetchTemplates();
    } else if (activeTab === 'asesores') {
      fetchAsesoresCNC();
      fetchUsuariosEditores();
      fetchInstituciones();
      fetchGrupos();
    } else if (activeTab === 'indicadores') {
      fetchIndicadoresBR();
    } else if (activeTab === 'asesores-probidad') {
      fetchAsesoresProbidad();
      fetchInstituciones();
    } else if (activeTab === 'bitacora') {
      fetchBitacora();
    } else if (activeTab === 'gestion-datos') {
      fetchEstadisticasSistema();
      fetchGestionesEliminadas();
    } else if (activeTab === 'calificaciones') {
      fetchCalificaciones();
    } else if (activeTab === 'grupos') {
      fetchGrupos();
      fetchInstituciones();
    }
  }, [activeTab]);

  // ============= USER MANAGEMENT =============
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users`, userForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario creado correctamente');
      setShowUserModal(false);
      resetUserForm();
      fetchUsers();
    } catch (error) {
      console.error('Error al crear usuario:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await axios.put(`${API}/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario actualizado correctamente');
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      fetchUsers();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Está seguro de eliminar al usuario ${userEmail}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario eliminado correctamente');
      fetchUsers();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${user.id}`, 
        { is_active: !user.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario ${user.is_active ? 'inactivado' : 'activado'} correctamente`);
      fetchUsers();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar estado del usuario');
    }
  };

  const openUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        email: user.email,
        full_name: user.full_name,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      resetUserForm();
    }
    setShowUserModal(true);
  };

  // Abrir modal cambio de email
  const openChangeEmailModal = (user) => {
    setChangeEmailUser(user);
    setNewEmail(user.email);
    setChangeEmailConfirm(false);
    setShowChangeEmailModal(true);
  };

  // Ejecutar cambio de email
  const handleChangeEmail = async () => {
    if (!changeEmailUser || !newEmail.trim()) return;
    if (newEmail === changeEmailUser.email) {
      toast.error('El nuevo correo es igual al actual');
      return;
    }
    if (!newEmail.includes('@')) {
      toast.error('Ingrese un correo electrónico válido');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.put(
        `${API}/users/${changeEmailUser.id}/change-email`,
        { new_email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(resp.data.message);
      setShowChangeEmailModal(false);
      setChangeEmailUser(null);
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al cambiar el correo';
      toast.error(msg);
    }
  };


  const resetUserForm = () => {
    setUserForm({
      email: '',
      full_name: '',
      password: '',
      role: 'viewer'
    });
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'editor': return 'role-editor';
      case 'analyst': return 'role-analyst';
      case 'viewer': return 'role-viewer';
      default: return 'role-default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'editor': return 'Edición';
      case 'viewer': return 'Consulta';
      default: return role;
    }
  };

  // ============= ASESORES CNC MANAGEMENT =============

  const fetchAsesoresCNC = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogos/asesores-cnc`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsesoresCNC(response.data || []);
    } catch (error) {
      console.error('Error al cargar asesores:', error);
      toast.error('Error al cargar asesores CNC');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsuariosEditores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Incluir TODOS los usuarios para poder vincularlos como asesores CNC
      // Antes solo permitía editor/analyst, ahora permite cualquier usuario
      const todosUsuarios = (response.data || []).filter(u => u.is_active !== false);
      setUsuariosEditores(todosUsuarios);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  const handleCreateAsesor = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/catalogos/asesores-cnc`, asesorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asesor CNC creado correctamente');
      setShowAsesorModal(false);
      resetAsesorForm();
      fetchAsesoresCNC();
    } catch (error) {
      console.error('Error al crear asesor:', error);
      toast.error(error.response?.data?.detail || 'Error al crear asesor');
    }
  };

  const handleUpdateAsesor = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/catalogos/asesores-cnc/${editingAsesor.id}`, asesorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asesor CNC actualizado correctamente');
      setShowAsesorModal(false);
      setEditingAsesor(null);
      resetAsesorForm();
      fetchAsesoresCNC();
    } catch (error) {
      console.error('Error al actualizar asesor:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar asesor');
    }
  };

  const handleDeleteAsesor = async (asesorId, nombre) => {
    if (!window.confirm(`¿Está seguro de desactivar al asesor ${nombre}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/catalogos/asesores-cnc/${asesorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asesor desactivado correctamente');
      fetchAsesoresCNC();
    } catch (error) {
      console.error('Error al desactivar asesor:', error);
      toast.error('Error al desactivar asesor');
    }
  };

  const openAsesorModal = (asesor = null) => {
    if (asesor) {
      setEditingAsesor(asesor);
      setAsesorForm({
        nombre_completo: asesor.nombre_completo,
        email: asesor.email,
        telefono: asesor.telefono || '',
        cargo: asesor.cargo || '',
        es_analista: asesor.es_analista !== false,
        user_id: asesor.user_id || '',
        grupo_id: asesor.grupo_id || '',
        instituciones_asignadas: asesor.instituciones_asignadas || [],
        activo: asesor.activo !== false
      });
    } else {
      setEditingAsesor(null);
      resetAsesorForm();
    }
    setShowAsesorModal(true);
  };

  const resetAsesorForm = () => {
    setAsesorForm({
      nombre_completo: '',
      email: '',
      telefono: '',
      cargo: '',
      es_analista: true,
      user_id: '',
      grupo_id: '',
      instituciones_asignadas: [],
      activo: true
    });
  };

  // ============= INDICADORES BANDERAS ROJAS MANAGEMENT =============

  const fetchIndicadoresBR = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogos/indicadores-banderas-rojas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndicadoresBR(response.data || []);
    } catch (error) {
      console.error('Error al cargar indicadores:', error);
      toast.error('Error al cargar indicadores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndicador = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/catalogos/indicadores-banderas-rojas`, indicadorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Indicador creado correctamente');
      setShowIndicadorModal(false);
      resetIndicadorForm();
      fetchIndicadoresBR();
    } catch (error) {
      console.error('Error al crear indicador:', error);
      toast.error(error.response?.data?.detail || 'Error al crear indicador');
    }
  };

  const handleUpdateIndicador = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/catalogos/indicadores-banderas-rojas/${editingIndicador.id}`, indicadorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Indicador actualizado correctamente');
      setShowIndicadorModal(false);
      setEditingIndicador(null);
      resetIndicadorForm();
      fetchIndicadoresBR();
    } catch (error) {
      console.error('Error al actualizar indicador:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar indicador');
    }
  };

  const handleDeleteIndicador = async (indicadorId, nombre) => {
    if (!window.confirm(`¿Está seguro de desactivar el indicador "${nombre}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/catalogos/indicadores-banderas-rojas/${indicadorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Indicador desactivado correctamente');
      fetchIndicadoresBR();
    } catch (error) {
      console.error('Error al desactivar indicador:', error);
      toast.error('Error al desactivar indicador');
    }
  };

  const openIndicadorModal = (indicador = null) => {
    if (indicador) {
      setEditingIndicador(indicador);
      setIndicadorForm({
        nombre_categoria: indicador.nombre_categoria,
        ejemplo_identificacion: indicador.ejemplo_identificacion,
        forma_medicion: indicador.forma_medicion,
        indicador: indicador.indicador,
        fuente_informacion: indicador.fuente_informacion,
        nivel_riesgo: indicador.nivel_riesgo,
        justificacion_contextual: indicador.justificacion_contextual
      });
    } else {
      setEditingIndicador(null);
      resetIndicadorForm();
    }
    setShowIndicadorModal(true);
  };

  const resetIndicadorForm = () => {
    setIndicadorForm({
      nombre_categoria: '',
      ejemplo_identificacion: '',
      forma_medicion: '',
      indicador: '',
      fuente_informacion: '',
      nivel_riesgo: 'Medio',
      justificacion_contextual: ''
    });
  };

  // ============= ASESORES PROBIDAD =============
  
  const fetchAsesoresProbidad = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/asignacion/asesores-probidad`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsesoresProbidad(response.data || []);
    } catch (error) {
      console.error('Error al cargar asesores de probidad:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstituciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogs/institutions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstituciones((response.data || []).filter(i => i.is_active));
    } catch (error) {
      console.error('Error al cargar instituciones:', error);
    }
  };

  const handleCreateAsesorProbidad = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/asignacion/asesores-probidad`, asesorProbidadForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asesor de probidad creado correctamente');
      setShowAsesorProbidadModal(false);
      resetAsesorProbidadForm();
      fetchAsesoresProbidad();
    } catch (error) {
      console.error('Error al crear asesor:', error);
      toast.error(error.response?.data?.detail || 'Error al crear asesor');
    }
  };

  const handleUpdateAsesorProbidad = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/asignacion/asesores-probidad/${editingAsesorProbidad.id}`, asesorProbidadForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asesor actualizado correctamente');
      setShowAsesorProbidadModal(false);
      setEditingAsesorProbidad(null);
      fetchAsesoresProbidad();
    } catch (error) {
      console.error('Error al actualizar asesor:', error);
      toast.error('Error al actualizar asesor');
    }
  };

  const handleDeleteAsesorProbidad = async (asesorId, nombre) => {
    if (!window.confirm(`¿Está seguro de desactivar al asesor "${nombre}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/asignacion/asesores-probidad/${asesorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asesor desactivado');
      fetchAsesoresProbidad();
    } catch (error) {
      toast.error('Error al desactivar asesor');
    }
  };

  const openAsesorProbidadModal = (asesor = null) => {
    if (asesor) {
      setEditingAsesorProbidad(asesor);
      setAsesorProbidadForm({
        nombre_completo: asesor.nombre_completo,
        email: asesor.email,
        telefono: asesor.telefono || '',
        cargo: asesor.cargo || '',
        institucion_id: asesor.institucion_id,
        activo: asesor.activo !== false
      });
    } else {
      setEditingAsesorProbidad(null);
      resetAsesorProbidadForm();
    }
    setShowAsesorProbidadModal(true);
  };

  const resetAsesorProbidadForm = () => {
    setAsesorProbidadForm({
      nombre_completo: '',
      email: '',
      telefono: '',
      cargo: '',
      institucion_id: '',
      activo: true
    });
  };

  // ============= GRUPOS DE INSTITUCIONES =============
  
  const fetchGrupos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/grupos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrupos(response.data || []);
    } catch (error) {
      console.error('Error al cargar grupos:', error);
    }
  };

  const handleCreateGrupo = async () => {
    if (!grupoForm.nombre.trim()) {
      toast.error('El nombre del grupo es obligatorio');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/grupos`, {
        nombre: grupoForm.nombre,
        instituciones_ids: grupoForm.instituciones_ids
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Grupo creado correctamente');
      setShowGrupoModal(false);
      resetGrupoForm();
      fetchGrupos();
    } catch (error) {
      console.error('Error al crear grupo:', error);
      toast.error(error.response?.data?.detail || 'Error al crear grupo');
    }
  };

  const handleUpdateGrupo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/grupos/${editingGrupo.id}`, {
        nombre: grupoForm.nombre,
        instituciones_ids: grupoForm.instituciones_ids
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Grupo actualizado');
      setShowGrupoModal(false);
      setEditingGrupo(null);
      resetGrupoForm();
      fetchGrupos();
    } catch (error) {
      console.error('Error al actualizar grupo:', error);
      toast.error('Error al actualizar grupo');
    }
  };

  const handleDeleteGrupo = async (grupoId, nombre) => {
    if (!window.confirm(`¿Está seguro de desactivar el grupo "${nombre}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/grupos/${grupoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Grupo desactivado');
      fetchGrupos();
    } catch (error) {
      toast.error('Error al desactivar grupo');
    }
  };

  // Eliminar PERMANENTEMENTE un grupo inactivo (Mejora 4)
  const handleDeleteGrupoPermanente = async (grupoId, nombre) => {
    if (!window.confirm(`⚠️ ATENCIÓN: Esta acción eliminará PERMANENTEMENTE el grupo "${nombre}". Esta acción NO se puede deshacer. ¿Desea continuar?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/grupos/${grupoId}/permanente`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Grupo eliminado permanentemente');
      fetchGrupos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar grupo permanentemente');
    }
  };

  // Eliminar PERMANENTEMENTE un indicador inactivo (Mejora 4)
  const handleDeleteIndicadorPermanente = async (indicadorId, nombre) => {
    if (!window.confirm(`⚠️ ATENCIÓN: Esta acción eliminará PERMANENTEMENTE el indicador "${nombre}". Esta acción NO se puede deshacer. ¿Desea continuar?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/indicadores/${indicadorId}/permanente`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Indicador eliminado permanentemente');
      fetchIndicadoresBR();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar indicador permanentemente');
    }
  };

  const openGrupoModal = (grupo = null) => {
    if (grupo) {
      setEditingGrupo(grupo);
      setGrupoForm({
        nombre: grupo.nombre,
        instituciones_ids: grupo.instituciones?.map(i => i.id) || []
      });
    } else {
      setEditingGrupo(null);
      resetGrupoForm();
    }
    setShowGrupoModal(true);
  };

  const resetGrupoForm = () => {
    setGrupoForm({
      nombre: '',
      instituciones_ids: []
    });
  };

  // ============= BITÁCORA =============
  
  const fetchBitacora = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/bitacora?limite=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBitacora(response.data || { entradas_gestiones: [], eventos_sistema: [] });
    } catch (error) {
      console.error('Error al cargar bitácora:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============= GESTIÓN DE DATOS =============
  
  const fetchEstadisticasSistema = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/estadisticas-sistema`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstadisticasSistema(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const fetchGestionesEliminadas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/gestiones-eliminadas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGestionesEliminadas(response.data || []);
    } catch (error) {
      console.error('Error al cargar gestiones eliminadas:', error);
    }
  };

  const handleLimpiarDatos = async (tipo) => {
    if (!window.confirm(`¿Está seguro de limpiar los datos de tipo "${tipo}"? Esta acción no se puede deshacer.`)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/limpiar-datos?tipo=${tipo}&confirmar=true`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.descripcion || 'Datos limpiados');
      fetchEstadisticasSistema();
      fetchGestionesEliminadas();
    } catch (error) {
      toast.error('Error al limpiar datos');
    }
  };

  const handleRestaurarGestion = async (gestionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/gestiones/${gestionId}/restaurar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gestión restaurada');
      fetchGestionesEliminadas();
      fetchEstadisticasSistema();
    } catch (error) {
      toast.error('Error al restaurar gestión');
    }
  };

  const handleEliminarPermanente = async (gestionId) => {
    if (!window.confirm('¿Eliminar PERMANENTEMENTE esta gestión? No se puede recuperar.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/gestiones/${gestionId}?permanente=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gestión eliminada permanentemente');
      fetchGestionesEliminadas();
      fetchEstadisticasSistema();
    } catch (error) {
      toast.error('Error al eliminar gestión');
    }
  };

  const handleLimpiarTodasGestiones = async () => {
    const confirmacion1 = window.confirm('⚠️ ADVERTENCIA: Esta acción eliminará TODAS las gestiones de forma PERMANENTE.\n\n¿Está seguro?');
    if (!confirmacion1) return;
    
    const confirmacion2 = window.prompt('Para confirmar, escriba "ELIMINAR TODO" (sin comillas):');
    if (confirmacion2 !== 'ELIMINAR TODO') {
      toast.error('Confirmación incorrecta. Operación cancelada.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/admin/limpiar-todas-gestiones?confirmar=CONFIRMAR_ELIMINACION`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Se eliminaron ${response.data.gestiones_eliminadas} gestiones`);
      fetchEstadisticasSistema();
      fetchGestionesEliminadas();
    } catch (error) {
      toast.error('Error al limpiar la base de datos');
    }
  };

  // ============= CALIFICACIONES =============
  
  const fetchCalificaciones = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [instRes, aseRes] = await Promise.all([
        axios.get(`${API}/admin/calificaciones/instancias`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/calificaciones/asesores`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCalificacionesInstancias(instRes.data || { calificaciones: [], promedios: [] });
      setCalificacionesAsesores(aseRes.data || { calificaciones: [], promedios: [] });
    } catch (error) {
      console.error('Error al cargar calificaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============= CATALOG MANAGEMENT =============
  
  const catalogTypeMap = {
    institutions: 'institutions',
    categories: 'categories',
    networks: 'networks',
    persons: 'persons',
    statuses: 'statuses',
    tipo_concurso: 'tipo_concurso',
    estado_concurso: 'estado_concurso',
    cuentas_sociales: 'cuentas_sociales'
  };

  const catalogLabels = {
    institutions: 'Instituciones',
    categories: 'Categorías',
    networks: 'Redes Sociales',
    persons: 'Personas',
    statuses: 'Estados',
    tipo_concurso: 'Tipo de Concurso (Adquisiciones)',
    estado_concurso: 'Estado de Concurso (Adquisiciones)',
    cuentas_sociales: 'Cuentas / Autores (Monitoreo)'
  };

  const fetchAllCatalogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      for (const [key, catalogType] of Object.entries(catalogTypeMap)) {
        const response = await axios.get(`${API}/catalogs/${catalogType}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { include_inactive: true }
        });
        setCatalogs(prev => ({ ...prev, [key]: response.data || [] }));
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
      toast.error('Error al cargar catálogos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCatalogItem = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        catalog_type: catalogTypeMap[selectedCatalog],
        value: catalogForm.value,
        order: catalogForm.order
      };
      // Agregar red_social solo para cuentas_sociales
      if (selectedCatalog === 'cuentas_sociales' && catalogForm.red_social) {
        payload.red_social = catalogForm.red_social;
      }
      await axios.post(`${API}/catalogs`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Elemento agregado correctamente');
      setShowCatalogModal(false);
      resetCatalogForm();
      fetchAllCatalogs();
    } catch (error) {
      console.error('Error al crear elemento:', error);
      toast.error('Error al crear elemento');
    }
  };

  const handleUpdateCatalogItem = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        value: catalogForm.value,
        order: catalogForm.order,
        is_active: catalogForm.is_active
      };
      // Agregar red_social solo para cuentas_sociales
      if (selectedCatalog === 'cuentas_sociales') {
        payload.red_social = catalogForm.red_social || null;
      }
      await axios.put(`${API}/catalogs/${editingCatalog.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Elemento actualizado correctamente');
      setShowCatalogModal(false);
      setEditingCatalog(null);
      resetCatalogForm();
      fetchAllCatalogs();
    } catch (error) {
      console.error('Error al actualizar elemento:', error);
      toast.error('Error al actualizar elemento');
    }
  };

  const handleDeleteCatalogItem = async (itemId, itemValue) => {
    if (!window.confirm(`¿Está seguro de eliminar "${itemValue}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/catalogs/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Elemento eliminado correctamente');
      fetchAllCatalogs();
    } catch (error) {
      console.error('Error al eliminar elemento:', error);
      toast.error('Error al eliminar elemento');
    }
  };

  const handleToggleCatalogStatus = async (item) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/catalogs/${item.id}`, 
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Elemento ${item.is_active ? 'inactivado' : 'activado'} correctamente`);
      fetchAllCatalogs();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar estado del elemento');
    }
  };

  const openCatalogModal = (item = null) => {
    if (item) {
      setEditingCatalog(item);
      setCatalogForm({
        value: item.value,
        order: item.order,
        is_active: item.is_active,
        red_social: item.red_social || ''
      });
    } else {
      setEditingCatalog(null);
      resetCatalogForm();
    }
    setShowCatalogModal(true);
  };

  const resetCatalogForm = () => {
    setCatalogForm({
      value: '',
      order: 0,
      is_active: true,
      red_social: ''
    });
  };

  // ============= TEXT TEMPLATE MANAGEMENT =============
  
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/text-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/text-templates/${editingTemplate.id}`, 
        { content: templateForm.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Plantilla actualizada correctamente');
      setShowTemplateModal(false);
      setEditingTemplate(null);
      resetTemplateForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error al actualizar plantilla:', error);
      toast.error('Error al actualizar plantilla');
    }
  };

  const openTemplateModal = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      content: template.content
    });
    setShowTemplateModal(true);
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      content: ''
    });
  };

  const getCategoryLabel = (category) => {
    return category === 'share' ? 'Compartir' : 'PDF';
  };

  const getCategoryBadgeClass = (category) => {
    return category === 'share' ? 'category-share' : 'category-pdf';
  };

  // Generar PDF de ficha de indicador de bandera roja
  const handleGenerarPDFIndicador = (indicador) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header con logo/título
    doc.setFillColor(244, 67, 54); // Rojo
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('FICHA DE INDICADOR', pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(14);
    doc.text('BANDERA ROJA', pageWidth / 2, 28, { align: 'center' });
    
    // Código del indicador
    doc.setFontSize(12);
    doc.text(indicador.codigo || 'N/A', pageWidth / 2, 36, { align: 'center' });
    
    let yPos = 55;
    doc.setTextColor(0, 0, 0);
    
    // Función helper para agregar sección
    const addSection = (label, value, isMultiline = false) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(244, 67, 54);
      doc.text(label, 14, yPos);
      yPos += 6;
      
      doc.setFont(undefined, 'normal');
      doc.setTextColor(51, 51, 51);
      
      if (isMultiline && value) {
        const lines = doc.splitTextToSize(String(value), pageWidth - 28);
        doc.text(lines, 14, yPos);
        yPos += lines.length * 5 + 8;
      } else {
        doc.text(String(value || 'N/A'), 14, yPos);
        yPos += 12;
      }
    };
    
    // Información del indicador
    addSection('NOMBRE CATEGORÍA:', indicador.nombre_categoria);
    
    // Nivel de riesgo con color
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(244, 67, 54);
    doc.text('NIVEL DE RIESGO:', 14, yPos);
    yPos += 6;
    
    const nivelColor = indicador.nivel_riesgo === 'Alto' ? [244, 67, 54] : 
                       indicador.nivel_riesgo === 'Medio' ? [255, 152, 0] : [76, 175, 80];
    doc.setFillColor(...nivelColor);
    doc.roundedRect(14, yPos - 4, 30, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(indicador.nivel_riesgo || 'N/A', 29, yPos + 1, { align: 'center' });
    yPos += 15;
    
    addSection('INDICADOR:', indicador.indicador, true);
    addSection('EJEMPLO DE IDENTIFICACIÓN:', indicador.ejemplo_identificacion, true);
    addSection('FORMA DE MEDICIÓN:', indicador.forma_medicion, true);
    addSection('FUENTE DE INFORMACIÓN:', indicador.fuente_informacion, true);
    addSection('JUSTIFICACIÓN CONTEXTUAL:', indicador.justificacion_contextual, true);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generado: ${new Date().toLocaleString('es-GT')}`, 14, 285);
    doc.text('Comisión Nacional Contra la Corrupción (CNC)', pageWidth - 14, 285, { align: 'right' });
    
    // Guardar
    doc.save(`Ficha_Indicador_${indicador.codigo || indicador.id}.pdf`);
    toast.success('PDF de ficha generado correctamente');
  };

  return (
    <>
    <div className="admin-container fade-in" data-testid="admin-view">
      <div className="container">
        <div className="panel">
          <div className="admin-header">
            <div>
              <h2 className="panel-title">
                <Shield size={24} />
                Administración del Sistema
              </h2>
              <p className="admin-subtitle">
                Gestión de usuarios y catálogos del sistema
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="admin-tabs" style={{ flexWrap: 'wrap' }}>
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            data-testid="users-tab"
          >
            <Users size={20} />
            Usuarios
          </button>
          <button
            className={`admin-tab ${activeTab === 'grupos' ? 'active' : ''}`}
            onClick={() => setActiveTab('grupos')}
            data-testid="grupos-tab"
          >
            <Users size={20} />
            Grupos
          </button>
          <button
            className={`admin-tab ${activeTab === 'catalogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('catalogs')}
            data-testid="catalogs-tab"
          >
            <Settings size={20} />
            Catálogos
          </button>
          <button
            className={`admin-tab ${activeTab === 'asesores' ? 'active' : ''}`}
            onClick={() => setActiveTab('asesores')}
            data-testid="asesores-tab"
          >
            <User size={20} />
            Asesores CNC
          </button>
          <button
            className={`admin-tab ${activeTab === 'asesores-probidad' ? 'active' : ''}`}
            onClick={() => setActiveTab('asesores-probidad')}
          >
            <Users size={20} />
            Asesores Probidad
          </button>
          <button
            className={`admin-tab ${activeTab === 'indicadores' ? 'active' : ''}`}
            onClick={() => setActiveTab('indicadores')}
            data-testid="indicadores-tab"
          >
            <Shield size={20} />
            Indicadores BR
          </button>
          <button
            className={`admin-tab ${activeTab === 'bitacora' ? 'active' : ''}`}
            onClick={() => setActiveTab('bitacora')}
          >
            <FileText size={20} />
            Bitácora
          </button>
          <button
            className={`admin-tab ${activeTab === 'gestion-datos' ? 'active' : ''}`}
            onClick={() => setActiveTab('gestion-datos')}
          >
            <Settings size={20} />
            Gestión Datos
          </button>
          <button
            className={`admin-tab ${activeTab === 'calificaciones' ? 'active' : ''}`}
            onClick={() => setActiveTab('calificaciones')}
          >
            <CheckCircle size={20} />
            Calificaciones
          </button>
          <button
            className={`admin-tab ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveTab('templates')}
            data-testid="templates-tab"
          >
            <FileText size={20} />
            Textos
          </button>
        </div>

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="admin-content">
            <div className="panel">
              <div className="admin-toolbar">
                <h3>Usuarios del Sistema</h3>
                <div className="toolbar-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={fetchUsers}
                    disabled={loading}
                    data-testid="refresh-users-btn"
                  >
                    <RefreshCw size={16} />
                    Actualizar
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openUserModal()}
                    data-testid="add-user-btn"
                  >
                    <Plus size={16} />
                    Nuevo Usuario
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loader"></div>
                  <p>Cargando usuarios...</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="admin-table" data-testid="users-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Nombre Completo</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Último Acceso</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="empty-state">
                            No hay usuarios registrados
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr key={user.id}>
                            <td className="user-email">{user.email}</td>
                            <td>{user.full_name}</td>
                            <td>
                              <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                                {getRoleLabel(user.role)}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                                {user.is_active ? (
                                  <>
                                    <CheckCircle size={14} />
                                    Activo
                                  </>
                                ) : (
                                  <>
                                    <XCircle size={14} />
                                    Inactivo
                                  </>
                                )}
                              </span>
                            </td>
                            <td className="date-cell">
                              {user.lastLogin ? new Date(user.lastLogin).toLocaleString('es-ES') : 'Nunca'}
                            </td>
                            <td className="actions-cell">
                              <button
                                className="btn-icon"
                                onClick={() => openChangeEmailModal(user)}
                                title="Cambiar correo"
                                data-testid={`change-email-${user.email}`}
                              >
                                <Mail size={16} />
                              </button>
                              <button
                                className="btn-icon"
                                onClick={() => openUserModal(user)}
                                title="Editar"
                                data-testid={`edit-user-${user.email}`}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="btn-icon"
                                onClick={() => handleToggleUserStatus(user)}
                                title={user.is_active ? 'Inactivar' : 'Activar'}
                                data-testid={`toggle-user-${user.email}`}
                              >
                                {user.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteUser(user.id, user.email)}
                                title="Eliminar"
                                data-testid={`delete-user-${user.email}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Grupos Management Tab */}
        {activeTab === 'grupos' && (
          <div className="admin-content">
            <div className="panel">
              <div className="admin-toolbar">
                <h3>Grupos de Instituciones</h3>
                <div className="toolbar-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={fetchGrupos}
                    disabled={loading}
                  >
                    <RefreshCw size={16} />
                    Actualizar
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openGrupoModal()}
                    data-testid="add-grupo-btn"
                  >
                    <Plus size={16} />
                    Nuevo Grupo
                  </button>
                </div>
              </div>

              <p style={{ marginBottom: '1rem', color: 'var(--color-text-muted)' }}>
                Los grupos permiten organizar instituciones y asignarlas a asesores CNC para una gestión más eficiente.
              </p>

              {loading ? (
                <div className="loading-state">
                  <div className="loader"></div>
                  <p>Cargando grupos...</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="admin-table" data-testid="grupos-table">
                    <thead>
                      <tr>
                        <th>Nombre del Grupo</th>
                        <th>Instituciones Asignadas</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupos.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="empty-state">
                            No hay grupos registrados
                          </td>
                        </tr>
                      ) : (
                        grupos.map((grupo) => (
                          <tr key={grupo.id}>
                            <td><strong>{grupo.nombre}</strong></td>
                            <td>
                              {grupo.instituciones?.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                  {grupo.instituciones.slice(0, 3).map((inst, idx) => (
                                    <span key={idx} className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                                      {inst.nombre}
                                    </span>
                                  ))}
                                  {grupo.instituciones.length > 3 && (
                                    <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>
                                      +{grupo.instituciones.length - 3} más
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: 'var(--color-text-muted)' }}>Sin instituciones</span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge ${grupo.activo ? 'status-active' : 'status-inactive'}`}>
                                {grupo.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="actions-cell">
                              <button
                                className="btn-icon"
                                onClick={() => openGrupoModal(grupo)}
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                              {grupo.activo ? (
                                <button
                                  className="btn-icon btn-icon-danger"
                                  onClick={() => handleDeleteGrupo(grupo.id, grupo.nombre)}
                                  title="Desactivar"
                                >
                                  <Trash2 size={16} />
                                </button>
                              ) : (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleDeleteGrupoPermanente(grupo.id, grupo.nombre)}
                                  title="Eliminar Permanentemente"
                                  style={{ color: '#b71c1c', backgroundColor: '#ffebee' }}
                                  data-testid={`delete-grupo-permanente-${grupo.id}`}
                                >
                                  <XCircle size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Catalog Management Tab */}
        {activeTab === 'catalogs' && (
          <div className="admin-content">
            <div className="panel">
              <div className="catalog-selector">
                <label>Seleccionar Catálogo:</label>
                <select
                  value={selectedCatalog}
                  onChange={(e) => setSelectedCatalog(e.target.value)}
                  data-testid="catalog-selector"
                >
                  {Object.keys(catalogLabels).map(key => (
                    <option key={key} value={key}>
                      {catalogLabels[key]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-toolbar">
                <h3>{catalogLabels[selectedCatalog]}</h3>
                <div className="toolbar-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={fetchAllCatalogs}
                    disabled={loading}
                    data-testid="refresh-catalogs-btn"
                  >
                    <RefreshCw size={16} />
                    Actualizar
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openCatalogModal()}
                    data-testid="add-catalog-btn"
                  >
                    <Plus size={16} />
                    Agregar Elemento
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loader"></div>
                  <p>Cargando catálogos...</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="admin-table" data-testid="catalogs-table">
                    <thead>
                      <tr>
                        <th>Valor</th>
                        {selectedCatalog === 'cuentas_sociales' && <th>Red Social</th>}
                        <th>Orden</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogs[selectedCatalog].length === 0 ? (
                        <tr>
                          <td colSpan={selectedCatalog === 'cuentas_sociales' ? 5 : 4} className="empty-state">
                            No hay elementos en este catálogo
                          </td>
                        </tr>
                      ) : (
                        catalogs[selectedCatalog].map((item) => (
                          <tr key={item.id}>
                            <td className="catalog-value">{item.value}</td>
                            {selectedCatalog === 'cuentas_sociales' && <td>{item.red_social || '-'}</td>}
                            <td>{item.order}</td>
                            <td>
                              <span className={`status-badge ${item.is_active ? 'status-active' : 'status-inactive'}`}>
                                {item.is_active ? (
                                  <>
                                    <CheckCircle size={14} />
                                    Activo
                                  </>
                                ) : (
                                  <>
                                    <XCircle size={14} />
                                    Inactivo
                                  </>
                                )}
                              </span>
                            </td>
                            <td className="actions-cell">
                              <button
                                className="btn-icon"
                                onClick={() => openCatalogModal(item)}
                                title="Editar"
                                data-testid={`edit-catalog-${item.value}`}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="btn-icon"
                                onClick={() => handleToggleCatalogStatus(item)}
                                title={item.is_active ? 'Inactivar' : 'Activar'}
                                data-testid={`toggle-catalog-${item.value}`}
                              >
                                {item.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteCatalogItem(item.id, item.value)}
                                title="Eliminar"
                                data-testid={`delete-catalog-${item.value}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Text Templates Tab */}
        {activeTab === 'templates' && (
          <div className="admin-content">
            <div className="panel">
              <div className="admin-toolbar">
                <h3>Plantillas de Texto</h3>
                <div className="toolbar-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={fetchTemplates}
                    disabled={loading}
                    data-testid="refresh-templates-btn"
                  >
                    <RefreshCw size={16} />
                    Actualizar
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loader"></div>
                  <p>Cargando plantillas...</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="admin-table" data-testid="templates-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Categoría</th>
                        <th>Descripción</th>
                        <th>Última Actualización</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="empty-state">
                            No hay plantillas disponibles
                          </td>
                        </tr>
                      ) : (
                        templates.map((template) => (
                          <tr key={template.id}>
                            <td className="template-name">{template.name}</td>
                            <td>
                              <span className={`category-badge ${getCategoryBadgeClass(template.category)}`}>
                                {getCategoryLabel(template.category)}
                              </span>
                            </td>
                            <td className="template-description">{template.description}</td>
                            <td className="date-cell">
                              {new Date(template.updatedAt).toLocaleString('es-ES', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="actions-cell">
                              <button
                                className="btn-icon"
                                onClick={() => openTemplateModal(template)}
                                title="Editar"
                                data-testid={`edit-template-${template.key}`}
                              >
                                <Edit size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <User size={20} />
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowUserModal(false)}
                data-testid="close-user-modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>
                  <Mail size={16} />
                  Email *
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  placeholder="usuario@ejemplo.com"
                  disabled={editingUser !== null}
                  data-testid="user-email-input"
                />
              </div>

              <div className="form-group">
                <label>
                  <User size={16} />
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                  placeholder="Nombre completo"
                  data-testid="user-name-input"
                />
              </div>

              <div className="form-group">
                <label>
                  <Lock size={16} />
                  Contraseña {editingUser && '(dejar en blanco para no cambiar)'}
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  placeholder={editingUser ? 'Nueva contraseña (opcional)' : 'Contraseña'}
                  data-testid="user-password-input"
                />
              </div>

              <div className="form-group">
                <label>
                  <Shield size={16} />
                  Rol *
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                  data-testid="user-role-select"
                >
                  <option value="viewer">Consulta</option>
                  <option value="editor">Edición (Asesor CNC)</option>
                  <option value="analyst">Analista</option>
                  <option value="admin">Administrador</option>
                </select>
                <small style={{ color: 'var(--color-text-muted)', marginTop: '0.25rem', display: 'block' }}>
                  Consulta: Solo lectura. Edición: Crea casos, ve todo. Analista: Ve sus casos asignados, sin crear.
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowUserModal(false)}
                data-testid="cancel-user-btn"
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={editingUser ? handleUpdateUser : handleCreateUser}
                data-testid="save-user-btn"
              >
                <Save size={16} />
                {editingUser ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Catalog Modal */}
      {showCatalogModal && (
        <div className="modal-overlay" onClick={() => setShowCatalogModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Settings size={20} />
                {editingCatalog ? 'Editar Elemento' : 'Nuevo Elemento'}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowCatalogModal(false)}
                data-testid="close-catalog-modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Valor *</label>
                <input
                  type="text"
                  value={catalogForm.value}
                  onChange={(e) => setCatalogForm({...catalogForm, value: e.target.value})}
                  placeholder="Nombre del elemento"
                  data-testid="catalog-value-input"
                />
              </div>

              {/* Campo Red Social solo para cuentas_sociales */}
              {selectedCatalog === 'cuentas_sociales' && (
                <div className="form-group">
                  <label>Red Social</label>
                  <select
                    value={catalogForm.red_social}
                    onChange={(e) => setCatalogForm({...catalogForm, red_social: e.target.value})}
                    data-testid="catalog-redsocial-select"
                  >
                    <option value="">Seleccione red social</option>
                    {catalogs.networks.filter(n => n.is_active !== false).map(red => (
                      <option key={red.id} value={red.value}>{red.value}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Orden</label>
                <input
                  type="number"
                  value={catalogForm.order}
                  onChange={(e) => setCatalogForm({...catalogForm, order: parseInt(e.target.value) || 0})}
                  placeholder="0"
                  data-testid="catalog-order-input"
                />
              </div>

              {editingCatalog && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={catalogForm.is_active}
                      onChange={(e) => setCatalogForm({...catalogForm, is_active: e.target.checked})}
                      data-testid="catalog-active-checkbox"
                    />
                    Activo
                  </label>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCatalogModal(false)}
                data-testid="cancel-catalog-btn"
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={editingCatalog ? handleUpdateCatalogItem : handleCreateCatalogItem}
                data-testid="save-catalog-btn"
              >
                <Save size={16} />
                {editingCatalog ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FileText size={20} />
                Editar Plantilla: {editingTemplate?.name}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowTemplateModal(false)}
                data-testid="close-template-modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="template-info">
                <div className="info-row">
                  <strong>Categoría:</strong>
                  <span className={`category-badge ${getCategoryBadgeClass(editingTemplate?.category)}`}>
                    {getCategoryLabel(editingTemplate?.category)}
                  </span>
                </div>
                <div className="info-row">
                  <strong>Descripción:</strong>
                  <span>{editingTemplate?.description}</span>
                </div>
              </div>

              <div className="form-group">
                <label>Contenido de la Plantilla *</label>
                <textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                  placeholder="Ingrese el texto de la plantilla"
                  rows={8}
                  data-testid="template-content-input"
                  className="template-textarea"
                />
                <small className="helper-text">
                  Este texto se utilizará en {editingTemplate?.category === 'share' ? 'el compartir de publicaciones' : 'los PDF generados'}.
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowTemplateModal(false)}
                data-testid="cancel-template-btn"
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateTemplate}
                data-testid="save-template-btn"
              >
                <Save size={16} />
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============= ASESORES CNC TAB ============= */}
      {activeTab === 'asesores' && (
        <div className="admin-content">
          <div className="panel">
            <div className="admin-toolbar">
              <h3>Gestión de Asesores CNC</h3>
              <div className="toolbar-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={fetchAsesoresCNC}
                  disabled={loading}
                >
                  <RefreshCw size={16} />
                  Actualizar
                </button>
                <button 
                  className="btn btn-primary btn-sm" 
                  onClick={() => openAsesorModal()}
                  data-testid="create-asesor-btn"
                >
                  <Plus size={16} />
                  Nuevo Asesor
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loader"></div>
                <p>Cargando asesores...</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="admin-table" data-testid="asesores-table">
                  <thead>
                    <tr>
                      <th>Nombre Completo</th>
                      <th>Email</th>
                      <th>Usuario Vinculado</th>
                      <th>Grupo Asignado</th>
                      <th>Instituciones</th>
                      <th>Analista</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesoresCNC.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="empty-state">
                          No hay asesores registrados
                        </td>
                      </tr>
                    ) : (
                      asesoresCNC.map((asesor) => (
                        <tr key={asesor.id}>
                          <td><strong>{asesor.nombre_completo}</strong></td>
                          <td className="user-email" style={{ fontSize: '0.85rem' }}>{asesor.email}</td>
                          <td>
                            {asesor.user_id ? (
                              <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                                {usuariosEditores.find(u => u.id === asesor.user_id)?.full_name || 'Vinculado'}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>Sin vincular</span>
                            )}
                          </td>
                          <td>
                            {asesor.grupo_nombre ? (
                              <span className="badge badge-primary" style={{ fontSize: '0.7rem', background: '#1976D2', color: 'white' }}>
                                {asesor.grupo_nombre}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>Sin grupo</span>
                            )}
                          </td>
                          <td>
                            {asesor.grupo_id ? (
                              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                (Por grupo)
                              </span>
                            ) : asesor.instituciones_asignadas && asesor.instituciones_asignadas.length > 0 ? (
                              <span style={{ fontSize: '0.75rem' }}>
                                {asesor.instituciones_asignadas.length} inst.
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>Todas</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${asesor.es_analista !== false ? 'badge-success' : 'badge-secondary'}`} style={{ fontSize: '0.65rem' }}>
                              {asesor.es_analista !== false ? 'Sí' : 'No'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${asesor.activo ? 'status-active' : 'status-inactive'}`}>
                              {asesor.activo ? (
                                <>
                                  <CheckCircle size={14} />
                                  Activo
                                </>
                              ) : (
                                <>
                                  <XCircle size={14} />
                                  Inactivo
                                </>
                              )}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button 
                              className="btn-icon" 
                              onClick={() => openAsesorModal(asesor)}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            {asesor.activo && (
                              <button 
                                className="btn-icon btn-icon-danger" 
                                onClick={() => handleDeleteAsesor(asesor.id, asesor.nombre_completo)}
                                title="Desactivar"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Asesor */}
      {showAsesorModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{editingAsesor ? 'Editar Asesor CNC' : 'Nuevo Asesor CNC'}</h3>
              <button className="modal-close" onClick={() => setShowAsesorModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Vincular con Usuario del Sistema</label>
                <select
                  value={asesorForm.user_id || ''}
                  onChange={(e) => {
                    const selectedUser = usuariosEditores.find(u => u.id === e.target.value);
                    if (selectedUser) {
                      setAsesorForm({
                        ...asesorForm, 
                        user_id: e.target.value,
                        nombre_completo: selectedUser.full_name || asesorForm.nombre_completo,
                        email: selectedUser.email || asesorForm.email
                      });
                    } else {
                      setAsesorForm({...asesorForm, user_id: ''});
                    }
                  }}
                >
                  <option value="">-- Seleccionar usuario del sistema --</option>
                  {usuariosEditores.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.email} ({user.email})
                    </option>
                  ))}
                </select>
                <small style={{ color: 'var(--color-text-muted)' }}>Vincule un usuario del sistema a este asesor CNC</small>
              </div>
              
              <div className="form-group">
                <label>Nombre Completo *</label>
                <input
                  type="text"
                  value={asesorForm.nombre_completo}
                  onChange={(e) => setAsesorForm({...asesorForm, nombre_completo: e.target.value})}
                  placeholder="Nombre y apellido"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={asesorForm.email}
                  onChange={(e) => setAsesorForm({...asesorForm, email: e.target.value})}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={asesorForm.telefono}
                    onChange={(e) => setAsesorForm({...asesorForm, telefono: e.target.value})}
                    placeholder="5555-5555"
                  />
                </div>
                <div className="form-group">
                  <label>Cargo</label>
                  <input
                    type="text"
                    value={asesorForm.cargo}
                    onChange={(e) => setAsesorForm({...asesorForm, cargo: e.target.value})}
                    placeholder="Asesor Senior"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={asesorForm.es_analista !== false}
                    onChange={(e) => setAsesorForm({...asesorForm, es_analista: e.target.checked})}
                  />
                  Es Asesor Analista CNC (puede recibir asignación automática)
                </label>
              </div>

              {editingAsesor && (
                <div className="form-group">
                  <label style={{ fontWeight: 600 }}>Estado</label>
                  <div 
                    onClick={() => setAsesorForm({...asesorForm, activo: !asesorForm.activo})}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer',
                      padding: '0.75rem 1rem', borderRadius: '8px',
                      background: asesorForm.activo ? '#E8F5E9' : '#FFEBEE',
                      border: `2px solid ${asesorForm.activo ? '#4CAF50' : '#F44336'}`,
                      transition: 'all 0.2s ease'
                    }}
                    data-testid="toggle-activo-asesor-cnc"
                  >
                    <div style={{
                      width: '44px', height: '24px', borderRadius: '12px',
                      background: asesorForm.activo ? '#4CAF50' : '#BDBDBD',
                      position: 'relative', transition: 'background 0.2s ease'
                    }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%', background: 'white',
                        position: 'absolute', top: '2px',
                        left: asesorForm.activo ? '22px' : '2px',
                        transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }} />
                    </div>
                    <span style={{ fontWeight: 600, color: asesorForm.activo ? '#2E7D32' : '#C62828' }}>
                      {asesorForm.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label>Grupo Asignado</label>
                <select
                  value={asesorForm.grupo_id || ''}
                  onChange={(e) => setAsesorForm({...asesorForm, grupo_id: e.target.value})}
                >
                  <option value="">-- Sin grupo asignado --</option>
                  {grupos.filter(g => g.activo).map((grupo) => (
                    <option key={grupo.id} value={grupo.id}>
                      {grupo.nombre} ({grupo.instituciones?.length || 0} instituciones)
                    </option>
                  ))}
                </select>
                <small style={{ color: 'var(--color-text-muted)' }}>
                  El asesor recibirá casos de las instituciones de su grupo asignado
                </small>
              </div>
              
              <div className="form-group">
                <label>Instituciones Asignadas (Legado)</label>
                <small style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
                  Si el asesor tiene un grupo asignado, las instituciones se determinarán por el grupo. Esta sección es solo para compatibilidad.
                </small>
                <div style={{ 
                  maxHeight: '250px', 
                  overflowY: 'auto', 
                  border: '1px solid #E0E0E0', 
                  borderRadius: '8px', 
                  background: '#FAFAFA'
                }}>
                  {instituciones?.length > 0 ? (
                    [...instituciones]
                      .filter(i => i.is_active !== false)
                      .sort((a, b) => {
                        // Primero mostrar las seleccionadas
                        const aSelected = asesorForm.instituciones_asignadas?.includes(a.id) || asesorForm.instituciones_asignadas?.includes(a.value);
                        const bSelected = asesorForm.instituciones_asignadas?.includes(b.id) || asesorForm.instituciones_asignadas?.includes(b.value);
                        if (aSelected && !bSelected) return -1;
                        if (!aSelected && bSelected) return 1;
                        return (a.value || '').localeCompare(b.value || '');
                      })
                      .map((inst) => {
                        const instId = inst.id || inst.value;
                        const isSelected = asesorForm.instituciones_asignadas?.includes(inst.id) || asesorForm.instituciones_asignadas?.includes(inst.value);
                        return (
                          <div 
                            key={inst.id} 
                            onClick={() => {
                              if (isSelected) {
                                setAsesorForm({
                                  ...asesorForm,
                                  instituciones_asignadas: (asesorForm.instituciones_asignadas || []).filter(id => id !== inst.id && id !== inst.value)
                                });
                              } else {
                                setAsesorForm({
                                  ...asesorForm,
                                  instituciones_asignadas: [...(asesorForm.instituciones_asignadas || []), instId]
                                });
                              }
                            }}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: '0.6rem 1rem', 
                              cursor: 'pointer',
                              borderBottom: '1px solid #EEEEEE',
                              background: isSelected ? '#E3F2FD' : 'transparent',
                              transition: 'all 0.2s'
                            }}
                          >
                            <span style={{ 
                              fontSize: '0.85rem',
                              color: isSelected ? '#1565C0' : '#333',
                              fontWeight: isSelected ? 500 : 400
                            }}>
                              {inst.value || inst.nombre}
                            </span>
                            {/* Toggle Switch */}
                            <div style={{
                              width: '40px',
                              height: '22px',
                              borderRadius: '11px',
                              background: isSelected ? '#4CAF50' : '#BDBDBD',
                              position: 'relative',
                              transition: 'all 0.2s',
                              flexShrink: 0
                            }}>
                              <div style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: 'white',
                                position: 'absolute',
                                top: '2px',
                                left: isSelected ? '20px' : '2px',
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }} />
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', padding: '1rem', textAlign: 'center' }}>No hay instituciones disponibles</p>
                  )}
                </div>
                {(asesorForm.instituciones_asignadas?.length || 0) > 0 && (
                  <small style={{ color: '#1976D2', marginTop: '0.5rem', display: 'block' }}>
                    {asesorForm.instituciones_asignadas?.length || 0} instituciones seleccionadas
                  </small>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowAsesorModal(false)}
              >
                <X size={16} />
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={editingAsesor ? handleUpdateAsesor : handleCreateAsesor}
              >
                <Save size={16} />
                {editingAsesor ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============= INDICADORES BANDERAS ROJAS TAB ============= */}
      {activeTab === 'indicadores' && (
        <div className="admin-content">
          <div className="panel">
            <div className="admin-toolbar">
              <h3>Indicadores de Banderas Rojas</h3>
              <div className="toolbar-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={fetchIndicadoresBR}
                  disabled={loading}
                >
                  <RefreshCw size={16} />
                  Actualizar
                </button>
                <button 
                  className="btn btn-primary btn-sm" 
                  onClick={() => openIndicadorModal()}
                  data-testid="create-indicador-btn"
                >
                  <Plus size={16} />
                  Nuevo Indicador
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loader"></div>
                <p>Cargando indicadores...</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="admin-table" data-testid="indicadores-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Categoría</th>
                      <th>Indicador</th>
                      <th>Nivel Riesgo</th>
                      <th>Fuente</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indicadoresBR.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="empty-state">
                          No hay indicadores registrados
                        </td>
                      </tr>
                    ) : (
                      indicadoresBR.map((indicador) => (
                        <tr key={indicador.id}>
                          <td>
                            <span style={{ 
                              fontFamily: 'monospace', 
                              fontWeight: 600,
                              color: '#1976D2',
                              backgroundColor: '#E3F2FD',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}>
                              {indicador.codigo || 'N/A'}
                            </span>
                          </td>
                          <td><strong>{indicador.nombre_categoria}</strong></td>
                          <td className="catalog-value">{indicador.indicador?.substring(0, 50)}{indicador.indicador?.length > 50 ? '...' : ''}</td>
                          <td>
                            <span className={`role-badge ${
                              indicador.nivel_riesgo === 'Alto' ? 'role-admin' :
                              indicador.nivel_riesgo === 'Medio' ? 'role-editor' :
                              'role-viewer'
                            }`}>
                              {indicador.nivel_riesgo}
                            </span>
                          </td>
                          <td>{indicador.fuente_informacion}</td>
                          <td>
                            <span className={`status-badge ${indicador.activo ? 'status-active' : 'status-inactive'}`}>
                              {indicador.activo ? (
                                <>
                                  <CheckCircle size={14} />
                                  Activo
                                </>
                              ) : (
                                <>
                                  <XCircle size={14} />
                                  Inactivo
                                </>
                              )}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button 
                              className="btn-icon" 
                              onClick={() => handleGenerarPDFIndicador(indicador)}
                              title="Generar Ficha PDF"
                              style={{ color: '#F44336' }}
                            >
                              <FileText size={16} />
                            </button>
                            <button 
                              className="btn-icon" 
                              onClick={() => openIndicadorModal(indicador)}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            {indicador.activo ? (
                              <button 
                                className="btn-icon btn-icon-danger" 
                                onClick={() => handleDeleteIndicador(indicador.id, indicador.nombre_categoria)}
                                title="Desactivar"
                              >
                                <Trash2 size={16} />
                              </button>
                            ) : (
                              <button 
                                className="btn-icon"
                                onClick={() => handleDeleteIndicadorPermanente(indicador.id, indicador.nombre_categoria)}
                                title="Eliminar Permanentemente"
                                style={{ color: '#b71c1c', backgroundColor: '#ffebee' }}
                                data-testid={`delete-indicador-permanente-${indicador.id}`}
                              >
                                <XCircle size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Indicador */}
      {showIndicadorModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h3>{editingIndicador ? 'Editar Indicador' : 'Nuevo Indicador de Bandera Roja'}</h3>
              <button className="modal-close" onClick={() => setShowIndicadorModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Nombre Categoría *</label>
                  <input
                    type="text"
                    value={indicadorForm.nombre_categoria}
                    onChange={(e) => setIndicadorForm({...indicadorForm, nombre_categoria: e.target.value})}
                    placeholder="Ej: Fraccionamiento de compras"
                  />
                </div>
                <div className="form-group">
                  <label>Nivel de Riesgo *</label>
                  <select
                    value={indicadorForm.nivel_riesgo}
                    onChange={(e) => setIndicadorForm({...indicadorForm, nivel_riesgo: e.target.value})}
                  >
                    <option value="Alto">Alto</option>
                    <option value="Medio">Medio</option>
                    <option value="Bajo">Bajo</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Indicador *</label>
                <textarea
                  value={indicadorForm.indicador}
                  onChange={(e) => setIndicadorForm({...indicadorForm, indicador: e.target.value})}
                  placeholder="Descripción del indicador..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Ejemplo de Identificación *</label>
                <textarea
                  value={indicadorForm.ejemplo_identificacion}
                  onChange={(e) => setIndicadorForm({...indicadorForm, ejemplo_identificacion: e.target.value})}
                  placeholder="Ejemplo de cómo identificar este indicador..."
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Forma de Medición *</label>
                <textarea
                  value={indicadorForm.forma_medicion}
                  onChange={(e) => setIndicadorForm({...indicadorForm, forma_medicion: e.target.value})}
                  placeholder="Cómo se mide este indicador..."
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Fuente de Información *</label>
                <input
                  type="text"
                  value={indicadorForm.fuente_informacion}
                  onChange={(e) => setIndicadorForm({...indicadorForm, fuente_informacion: e.target.value})}
                  placeholder="Ej: Guatecompras, MINFIN"
                />
              </div>

              <div className="form-group">
                <label>Justificación Contextual *</label>
                <textarea
                  value={indicadorForm.justificacion_contextual}
                  onChange={(e) => setIndicadorForm({...indicadorForm, justificacion_contextual: e.target.value})}
                  placeholder="Justificación del por qué es una bandera roja..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowIndicadorModal(false)}
              >
                <X size={16} />
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={editingIndicador ? handleUpdateIndicador : handleCreateIndicador}
              >
                <Save size={16} />
                {editingIndicador ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============= ASESORES PROBIDAD TAB ============= */}
      {activeTab === 'asesores-probidad' && (
        <div className="admin-content">
          <div className="panel">
            <div className="admin-toolbar">
              <h3>Asesores de Instancias de Probidad</h3>
              <div className="toolbar-actions">
                <button className="btn btn-secondary btn-sm" onClick={fetchAsesoresProbidad} disabled={loading}>
                  <RefreshCw size={16} /> Actualizar
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => openAsesorProbidadModal()}>
                  <Plus size={16} /> Nuevo Asesor
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading-state"><div className="loader"></div><p>Cargando...</p></div>
            ) : (
              <div className="table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Institución</th>
                      <th>Cargo</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesoresProbidad.length === 0 ? (
                      <tr><td colSpan="6" className="empty-state">No hay asesores registrados</td></tr>
                    ) : (
                      asesoresProbidad.map((asesor) => (
                        <tr key={asesor.id}>
                          <td><strong>{asesor.nombre_completo}</strong></td>
                          <td>{asesor.email}</td>
                          <td>{asesor.institucion_nombre}</td>
                          <td>{asesor.cargo || 'N/A'}</td>
                          <td>
                            <span className={`status-badge ${asesor.activo ? 'status-active' : 'status-inactive'}`}>
                              {asesor.activo ? <><CheckCircle size={14} /> Activo</> : <><XCircle size={14} /> Inactivo</>}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button className="btn-icon" onClick={() => openAsesorProbidadModal(asesor)} title="Editar">
                              <Edit size={16} />
                            </button>
                            {asesor.activo && (
                              <button className="btn-icon btn-icon-danger" onClick={() => handleDeleteAsesorProbidad(asesor.id, asesor.nombre_completo)} title="Desactivar">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Asesor Probidad */}
      {showAsesorProbidadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingAsesorProbidad ? 'Editar Asesor Probidad' : 'Nuevo Asesor de Probidad'}</h3>
              <button className="modal-close" onClick={() => setShowAsesorProbidadModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Institución *</label>
                <select value={asesorProbidadForm.institucion_id} onChange={(e) => setAsesorProbidadForm({...asesorProbidadForm, institucion_id: e.target.value})}>
                  <option value="">Seleccione institución</option>
                  {instituciones.map(inst => <option key={inst.id} value={inst.id}>{inst.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Nombre Completo *</label>
                <input type="text" value={asesorProbidadForm.nombre_completo} onChange={(e) => setAsesorProbidadForm({...asesorProbidadForm, nombre_completo: e.target.value})} placeholder="Nombre y apellido" />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={asesorProbidadForm.email} onChange={(e) => setAsesorProbidadForm({...asesorProbidadForm, email: e.target.value})} placeholder="correo@ejemplo.com" />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input type="text" value={asesorProbidadForm.telefono} onChange={(e) => setAsesorProbidadForm({...asesorProbidadForm, telefono: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Cargo</label>
                <input type="text" value={asesorProbidadForm.cargo} onChange={(e) => setAsesorProbidadForm({...asesorProbidadForm, cargo: e.target.value})} />
              </div>
              {editingAsesorProbidad && (
                <div className="form-group">
                  <label style={{ fontWeight: 600 }}>Estado</label>
                  <div 
                    onClick={() => setAsesorProbidadForm({...asesorProbidadForm, activo: !asesorProbidadForm.activo})}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer',
                      padding: '0.75rem 1rem', borderRadius: '8px',
                      background: asesorProbidadForm.activo ? '#E8F5E9' : '#FFEBEE',
                      border: `2px solid ${asesorProbidadForm.activo ? '#4CAF50' : '#F44336'}`,
                      transition: 'all 0.2s ease'
                    }}
                    data-testid="toggle-activo-asesor-probidad"
                  >
                    <div style={{
                      width: '44px', height: '24px', borderRadius: '12px',
                      background: asesorProbidadForm.activo ? '#4CAF50' : '#BDBDBD',
                      position: 'relative', transition: 'background 0.2s ease'
                    }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%', background: 'white',
                        position: 'absolute', top: '2px',
                        left: asesorProbidadForm.activo ? '22px' : '2px',
                        transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }} />
                    </div>
                    <span style={{ fontWeight: 600, color: asesorProbidadForm.activo ? '#2E7D32' : '#C62828' }}>
                      {asesorProbidadForm.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAsesorProbidadModal(false)}><X size={16} /> Cancelar</button>
              <button className="btn btn-primary" onClick={editingAsesorProbidad ? handleUpdateAsesorProbidad : handleCreateAsesorProbidad}>
                <Save size={16} /> {editingAsesorProbidad ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Grupo de Instituciones */}
      {showGrupoModal && (
        <div className="modal-overlay" onClick={() => setShowGrupoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1E3A5F, #2C5282)', color: 'white' }}>
              <h3 style={{ margin: 0 }}>{editingGrupo ? 'Editar Grupo' : 'Nuevo Grupo de Instituciones'}</h3>
              <button className="modal-close" onClick={() => setShowGrupoModal(false)} style={{ color: 'white' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Nombre del Grupo *</label>
                <input 
                  type="text" 
                  value={grupoForm.nombre} 
                  onChange={(e) => setGrupoForm({...grupoForm, nombre: e.target.value})} 
                  placeholder="Ej: Grupo Salud, Grupo Educación"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '1rem' }}
                />
              </div>
              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Instituciones Asignadas 
                  <span style={{ marginLeft: '0.5rem', background: '#E3F2FD', color: '#1976D2', padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 500 }}>
                    {grupoForm.instituciones_ids.length} seleccionadas
                  </span>
                </label>
                <div style={{ 
                  maxHeight: '320px', 
                  overflowY: 'auto', 
                  border: '1px solid #E0E0E0', 
                  borderRadius: '8px', 
                  background: '#FAFAFA'
                }}>
                  {[...instituciones]
                    .filter(i => i.is_active !== false)
                    .sort((a, b) => {
                      // Primero mostrar las seleccionadas
                      const aSelected = grupoForm.instituciones_ids.includes(a.id);
                      const bSelected = grupoForm.instituciones_ids.includes(b.id);
                      if (aSelected && !bSelected) return -1;
                      if (!aSelected && bSelected) return 1;
                      // Luego ordenar alfabéticamente
                      return (a.value || '').localeCompare(b.value || '');
                    })
                    .map(inst => {
                      const isSelected = grupoForm.instituciones_ids.includes(inst.id);
                      return (
                        <div 
                          key={inst.id} 
                          onClick={() => {
                            if (isSelected) {
                              setGrupoForm({...grupoForm, instituciones_ids: grupoForm.instituciones_ids.filter(id => id !== inst.id)});
                            } else {
                              setGrupoForm({...grupoForm, instituciones_ids: [...grupoForm.instituciones_ids, inst.id]});
                            }
                          }}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '0.75rem 1rem', 
                            cursor: 'pointer',
                            borderBottom: '1px solid #EEEEEE',
                            background: isSelected ? '#E3F2FD' : 'transparent',
                            transition: 'all 0.2s'
                          }}
                        >
                          <span style={{ 
                            fontSize: '0.9rem',
                            color: isSelected ? '#1565C0' : '#333',
                            fontWeight: isSelected ? 500 : 400
                          }}>
                            {inst.value}
                          </span>
                          {/* Toggle Switch */}
                          <div style={{
                            width: '44px',
                            height: '24px',
                            borderRadius: '12px',
                            background: isSelected ? '#4CAF50' : '#BDBDBD',
                            position: 'relative',
                            transition: 'all 0.2s',
                            flexShrink: 0
                          }}>
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: 'white',
                              position: 'absolute',
                              top: '2px',
                              left: isSelected ? '22px' : '2px',
                              transition: 'all 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGrupoModal(false)}><X size={16} /> Cancelar</button>
              <button className="btn btn-primary" onClick={editingGrupo ? handleUpdateGrupo : handleCreateGrupo}>
                <Save size={16} /> {editingGrupo ? 'Actualizar' : 'Crear Grupo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============= BITÁCORA TAB ============= */}
      {activeTab === 'bitacora' && (
        <div className="admin-content">
          <div className="panel">
            <div className="admin-toolbar">
              <h3>Bitácora de Auditoría</h3>
              <button className="btn btn-secondary btn-sm" onClick={fetchBitacora} disabled={loading}>
                <RefreshCw size={16} /> Actualizar
              </button>
            </div>

            {loading ? (
              <div className="loading-state"><div className="loader"></div><p>Cargando bitácora...</p></div>
            ) : (
              <>
                <h4 style={{ margin: '1rem 0 0.5rem' }}>Movimientos en Gestiones ({bitacora.entradas_gestiones?.length || 0})</h4>
                <div className="table-wrapper" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Gestión</th>
                        <th>Acción</th>
                        <th>Usuario</th>
                        <th>Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bitacora.entradas_gestiones || []).map((entrada, idx) => (
                        <tr key={idx}>
                          <td>{entrada.fecha ? new Date(entrada.fecha).toLocaleString('es-GT') : 'N/A'}</td>
                          <td><strong>{entrada.nomenclatura}</strong></td>
                          <td>{entrada.accion}</td>
                          <td>{entrada.usuario}</td>
                          <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entrada.detalles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h4 style={{ margin: '1.5rem 0 0.5rem' }}>Eventos del Sistema ({bitacora.eventos_sistema?.length || 0})</h4>
                <div className="table-wrapper" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Acción</th>
                        <th>Usuario</th>
                        <th>Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bitacora.eventos_sistema || []).map((evento, idx) => (
                        <tr key={idx}>
                          <td>{evento.fecha ? new Date(evento.fecha).toLocaleString('es-GT') : 'N/A'}</td>
                          <td>{evento.accion}</td>
                          <td>{evento.usuario}</td>
                          <td>{JSON.stringify(evento.detalles || {})}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ============= GESTIÓN DE DATOS TAB ============= */}
      {activeTab === 'gestion-datos' && (
        <div className="admin-content">
          <div className="panel">
            <div className="admin-toolbar">
              <h3>Gestión de Datos del Sistema</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => { fetchEstadisticasSistema(); fetchGestionesEliminadas(); }}>
                <RefreshCw size={16} /> Actualizar
              </button>
            </div>

            {/* Estadísticas */}
            {estadisticasSistema && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                {Object.entries(estadisticasSistema).map(([key, value]) => (
                  <div key={key} style={{ background: 'var(--color-background)', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-primary)' }}>{value}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{key.replace(/_/g, ' ')}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Acciones de limpieza */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <button className="btn btn-secondary" onClick={() => handleLimpiarDatos('scraping')}>
                Limpiar Scraping Procesados
              </button>
              <button className="btn btn-secondary" onClick={() => handleLimpiarDatos('contadores')}>
                Recalcular Contadores
              </button>
              <button className="btn btn-danger" onClick={() => handleLimpiarDatos('eliminados')}>
                Eliminar Permanente (Gestiones Eliminadas)
              </button>
            </div>

            {/* Exportar Gestiones */}
            <div style={{ 
              background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', 
              border: '1px solid #1565C0', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              marginBottom: '2rem' 
            }}>
              <h4 style={{ color: '#1565C0', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileDown size={18} /> Exportar Datos
              </h4>
              <p style={{ color: '#0D47A1', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Descargue el listado completo de gestiones en formato Excel (CSV). Los datos se exportan en tiempo real.
              </p>
              <button 
                data-testid="btn-exportar-gestiones"
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const resp = await axios.get(`${API}/gestiones`, { headers: { Authorization: `Bearer ${token}` } });
                    const gestiones = resp.data;
                    if (!gestiones || gestiones.length === 0) {
                      toast.error('No hay gestiones para exportar');
                      return;
                    }
                    
                    const headers = [
                      'Nomenclatura', 'Tipo Gestion', 'Institucion', 'Estado',
                      'Asesor CNC', 'Asesor Probidad', 'Fecha Asignacion', 'Fecha Vencimiento',
                      'Descripcion', 'Es Urgente', 'Resultado Final',
                      'Red Social', 'Autor', 'Tematica', 'Personas Mencionadas',
                      'Prorroga Dias', 'Prorroga Motivo', 'Creado Por', 'Fecha Creacion'
                    ];
                    
                    const escapeCsv = (val) => {
                      if (val === null || val === undefined) return '';
                      const str = String(val).replace(/<[^>]*>/g, '').replace(/\n/g, ' ');
                      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                        return '"' + str.replace(/"/g, '""') + '"';
                      }
                      return str;
                    };
                    
                    const rows = gestiones.map(g => {
                      const ce = g.campos_especificos || {};
                      return [
                        g.nomenclatura, g.tipo_gestion, g.institucion, g.estado,
                        g.nombre_asesor_cnc, g.asesor_probidad_nombre || g.nombre_asesor_instancia || '',
                        g.fecha_asignacion, g.fecha_vencimiento || '',
                        (g.descripcion || '').replace(/<[^>]*>/g, '').substring(0, 500),
                        g.es_urgente ? 'Si' : 'No', g.resultado_final || '',
                        ce.network || '', ce.author || '', ce.tematica || '', ce.persons || '',
                        g.prorroga_probidad || '', g.prorroga_probidad_motivo || '',
                        g.createdBy || '', g.createdAt ? new Date(g.createdAt).toLocaleDateString('es-GT') : ''
                      ].map(escapeCsv);
                    });
                    
                    const BOM = '\uFEFF';
                    const csvContent = BOM + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    const fecha = new Date().toISOString().split('T')[0];
                    link.href = url;
                    link.download = `Gestiones_ALERTA360_${fecha}.csv`;
                    link.click();
                    URL.revokeObjectURL(url);
                    toast.success(`${gestiones.length} gestiones exportadas exitosamente`);
                  } catch (error) {
                    console.error('Error al exportar:', error);
                    toast.error('Error al exportar gestiones');
                  }
                }}
                style={{ background: '#1565C0' }}
              >
                <FileDown size={16} /> Exportar Gestiones a CSV
              </button>
            </div>

            {/* Acción PELIGROSA - Limpiar toda la base de datos */}
            <div style={{ 
              background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)', 
              border: '2px solid #D32F2F', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              marginBottom: '2rem' 
            }}>
              <h4 style={{ color: '#D32F2F', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                ⚠️ Zona de Peligro
              </h4>
              <p style={{ color: '#B71C1C', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Esta acción eliminará <strong>TODAS</strong> las gestiones de forma permanente e irreversible. 
                Use solo para pruebas o cuando necesite reiniciar el sistema.
              </p>
              <button 
                className="btn" 
                style={{ backgroundColor: '#D32F2F', color: 'white' }}
                onClick={handleLimpiarTodasGestiones}
              >
                🗑️ Limpiar TODA la Base de Datos
              </button>
            </div>

            {/* Gestiones Eliminadas */}
            <h4>Gestiones Eliminadas ({gestionesEliminadas.length})</h4>
            {gestionesEliminadas.length > 0 ? (
              <div className="table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Nomenclatura</th>
                      <th>Tipo</th>
                      <th>Institución</th>
                      <th>Eliminado Por</th>
                      <th>Fecha Eliminación</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gestionesEliminadas.map(g => (
                      <tr key={g.id}>
                        <td><strong>{g.nomenclatura}</strong></td>
                        <td>{g.tipo_gestion}</td>
                        <td>{g.institucion}</td>
                        <td>{g.eliminado_por}</td>
                        <td>{g.eliminado_fecha ? new Date(g.eliminado_fecha).toLocaleString('es-GT') : 'N/A'}</td>
                        <td className="actions-cell">
                          <button className="btn btn-sm btn-primary" onClick={() => handleRestaurarGestion(g.id)}>Restaurar</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleEliminarPermanente(g.id)}>Eliminar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--color-text-muted)' }}>No hay gestiones eliminadas</p>
            )}
          </div>
        </div>
      )}

      {/* ============= CALIFICACIONES TAB ============= */}
      {activeTab === 'calificaciones' && (
        <div className="admin-content">
          <div className="panel">
            <div className="admin-toolbar">
              <h3>Reporte de Calificaciones a Instancias de Probidad</h3>
              <div className="toolbar-actions">
                <button 
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    // Exportar a Excel
                    if (!calificacionesInstancias.calificaciones?.length) {
                      alert('No hay datos para exportar');
                      return;
                    }
                    import('xlsx').then(XLSX => {
                      const data = calificacionesInstancias.calificaciones.map(cal => ({
                        'Código': cal.nomenclatura || 'N/A',
                        'Institución (Probidad)': cal.institucion || 'N/A',
                        'Asesor Probidad': cal.asesor_probidad_nombre || 'N/A',
                        'Tipo Gestión': cal.tipo_gestion || 'N/A',
                        'Calificación': cal.calificacion || 0,
                        'Observaciones': cal.observacion || '',
                        'Días de Gestión': cal.dias_gestion || 0,
                        'Calificado Por': cal.calificado_por || 'N/A',
                        'Fecha': cal.createdAt ? new Date(cal.createdAt).toLocaleDateString('es-GT') : 'N/A'
                      }));
                      const ws = XLSX.utils.json_to_sheet(data);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Calificaciones');
                      const fecha = new Date().toISOString().split('T')[0];
                      XLSX.writeFile(wb, `Calificaciones_Probidad_${fecha}.xlsx`);
                    });
                  }}
                >
                  <FileDown size={16} /> Exportar Excel
                </button>
                <button className="btn btn-secondary btn-sm" onClick={fetchCalificaciones} disabled={loading}>
                  <RefreshCw size={16} /> Actualizar
                </button>
              </div>
            </div>

            {/* Resumen de Promedios */}
            <div style={{ 
              background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
              border: '1px solid #4CAF50',
              borderRadius: '8px',
              padding: '1rem 1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#2E7D32', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Star size={18} /> Promedios por Institución
              </h4>
              {calificacionesInstancias.promedios?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                  {calificacionesInstancias.promedios.map((p, idx) => (
                    <div key={idx} style={{ 
                      background: 'white', 
                      padding: '0.5rem 1rem', 
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <span style={{ fontWeight: 500 }}>{p._id}</span>
                      <span style={{ color: '#FFB800', fontSize: '1.1rem' }}>{'★'.repeat(Math.round(p.promedio))}</span>
                      <span style={{ fontWeight: 700, color: '#2E7D32' }}>{p.promedio?.toFixed(1)}</span>
                      <span style={{ color: '#666', fontSize: '0.8rem' }}>({p.total} eval.)</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, color: '#666' }}>Sin calificaciones registradas</p>
              )}
            </div>

            {loading ? (
              <div className="loading-state"><div className="loader"></div><p>Cargando...</p></div>
            ) : (
              <>
                {/* Tabla detallada de Calificaciones */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0 }}>
                      Detalle de Calificaciones ({calificacionesInstancias.calificaciones?.length || 0} registros)
                    </h4>
                  </div>
                  
                  {calificacionesInstancias.calificaciones?.length > 0 ? (
                    <div className="table-wrapper" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <table className="admin-table" style={{ fontSize: '0.9rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                          <tr>
                            <th style={{ width: '100px' }}>Código</th>
                            <th>Institución (Probidad)</th>
                            <th>Asesor Probidad</th>
                            <th>Tipo Gestión</th>
                            <th style={{ width: '120px', textAlign: 'center' }}>Calificación</th>
                            <th>Observaciones</th>
                            <th style={{ width: '80px', textAlign: 'center' }}>Días</th>
                            <th style={{ width: '100px' }}>Fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calificacionesInstancias.calificaciones.map((cal, idx) => (
                            <tr key={idx}>
                              <td>
                                <code style={{ 
                                  fontSize: '0.85rem', 
                                  background: '#f0f0f0', 
                                  padding: '0.2rem 0.4rem', 
                                  borderRadius: '4px',
                                  fontWeight: 600
                                }}>
                                  {cal.nomenclatura || 'N/A'}
                                </code>
                              </td>
                              <td style={{ fontWeight: 500 }}>{cal.institucion || 'N/A'}</td>
                              <td>{cal.asesor_probidad_nombre || <span style={{ color: '#999' }}>-</span>}</td>
                              <td>
                                <span className="badge badge-info" style={{ fontSize: '0.75rem' }}>
                                  {cal.tipo_gestion || 'N/A'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                                  <span style={{ color: '#FFB800', fontSize: '1rem' }}>
                                    {'★'.repeat(cal.calificacion || 0)}
                                  </span>
                                  <span style={{ color: '#ddd', fontSize: '1rem' }}>
                                    {'★'.repeat(5 - (cal.calificacion || 0))}
                                  </span>
                                  <span style={{ 
                                    fontWeight: 700, 
                                    marginLeft: '0.25rem',
                                    color: cal.calificacion >= 4 ? '#4CAF50' : cal.calificacion >= 3 ? '#FF9800' : '#F44336'
                                  }}>
                                    {cal.calificacion || 0}
                                  </span>
                                </div>
                              </td>
                              <td style={{ maxWidth: '200px' }}>
                                <div style={{ 
                                  whiteSpace: 'nowrap', 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  fontSize: '0.85rem'
                                }} title={cal.observacion || ''}>
                                  {cal.observacion || <span style={{ color: '#999', fontStyle: 'italic' }}>Sin observaciones</span>}
                                </div>
                              </td>
                              <td style={{ textAlign: 'center', fontWeight: 500 }}>
                                {cal.dias_gestion || '-'}
                              </td>
                              <td style={{ fontSize: '0.8rem', color: '#666' }}>
                                {cal.createdAt ? new Date(cal.createdAt).toLocaleDateString('es-GT', { 
                                  day: '2-digit', 
                                  month: 'short', 
                                  year: 'numeric' 
                                }) : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '3rem', 
                      background: '#f9f9f9', 
                      borderRadius: '8px',
                      color: 'var(--color-text-muted)' 
                    }}>
                      <Star size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                      <p style={{ margin: '0 0 0.5rem 0', fontWeight: 500 }}>No hay calificaciones registradas</p>
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        Las calificaciones se generan al completar gestiones derivadas a Instancias de Probidad.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>

      {/* Modal Cambio de Email */}
      {showChangeEmailModal && changeEmailUser && (
        <div className="modal-overlay" onClick={() => setShowChangeEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #FF9800, #F57C00)', color: 'white' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <Mail size={20} /> Cambiar Correo Electronico
              </h3>
              <button className="modal-close" onClick={() => setShowChangeEmailModal(false)} style={{ color: 'white' }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#FFF3E0', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem', color: '#E65100' }}>
                <strong>Importante:</strong> Al cambiar el correo, el usuario debera iniciar sesion con el nuevo correo. Todo el historial y gestiones se mantendran vinculados automaticamente.
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Usuario
                </label>
                <div style={{ padding: '0.5rem 0.75rem', background: '#f5f5f5', borderRadius: '6px', fontSize: '0.9rem' }}>
                  {changeEmailUser.full_name || 'Sin nombre'} ({changeEmailUser.email})
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Correo actual
                </label>
                <input type="email" value={changeEmailUser.email} disabled
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #ddd', background: '#f9f9f9', color: '#888' }} />
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Nuevo correo *
                </label>
                <input
                  data-testid="new-email-input"
                  type="email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setChangeEmailConfirm(false); }}
                  placeholder="nuevo@correo.com"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>

              {newEmail && newEmail !== changeEmailUser.email && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                    <input
                      data-testid="confirm-change-checkbox"
                      type="checkbox"
                      checked={changeEmailConfirm}
                      onChange={(e) => setChangeEmailConfirm(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    Confirmo que deseo cambiar el correo de <strong>{changeEmailUser.email}</strong> a <strong>{newEmail}</strong>
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowChangeEmailModal(false)}>
                Cancelar
              </button>
              <button
                data-testid="btn-save-email-change"
                className="btn btn-primary"
                onClick={handleChangeEmail}
                disabled={!changeEmailConfirm || !newEmail || newEmail === changeEmailUser.email}
                style={{ background: changeEmailConfirm ? '#F57C00' : '#ccc' }}
              >
                <Save size={16} /> Guardar Cambio
              </button>
            </div>
          </div>
        </div>
      )}
  </>
  );
};

export default Admin;
